import { useTranslation } from "react-i18next";
import { convertDoubleToCurrencyPTBR } from "../../../../../utils/ConverterUtils";
import "./EmployeePaymentModal.css";

/**
 * Composição do valor do pagamento: as parcelas dos ganhos, os descontos e o líquido.
 *
 * Mostrada antes de pagar (com a prévia do servidor) e depois, nos detalhes do pagamento (com
 * a cópia gravada no momento do pagamento). Os números chegam prontos do servidor: o total e o
 * líquido não são recalculados aqui.
 *
 * @param {object} values - commissionServices, commissionProducts, subscriptionEarnings,
 *                          potBonus, bonuses, totalEarnings, advances, consumptions e netValue.
 */
const EmployeePaymentBreakdown = ({ values }) => {
    const { t } = useTranslation();
    const netValue = Number(values?.netValue || 0);
    // A prévia traz totalEarnings; o pagamento gravado guarda apenas as parcelas.
    const totalEarnings = values?.totalEarnings ?? [
        values?.commissionServices, values?.commissionProducts,
        values?.subscriptionEarnings, values?.potBonus, values?.bonuses
    ].reduce((total, value) => total + Number(value || 0), 0);

    /** Uma linha da composição; desconto sai com sinal e em vermelho, parcela zerada some. */
    const line = (label, value, { discount = false, hideWhenZero = false } = {}) => {
        if (hideWhenZero && Number(value || 0) === 0) {
            return null;
        }
        return (
            <div className={`employee-payment__line${discount ? ' is-discount' : ''}`}>
                <span>{label}</span>
                <strong>{discount ? `- ${convertDoubleToCurrencyPTBR(value)}` : convertDoubleToCurrencyPTBR(value)}</strong>
            </div>
        );
    };

    return (
        <div className="employee-payment__breakdown">
            {line(t('cash_flow.employee_payment_commission_services'), values.commissionServices)}
            {line(t('cash_flow.employee_payment_commission_products'), values.commissionProducts, { hideWhenZero: true })}
            {line(t('cash_flow.employee_payment_subscription'), values.subscriptionEarnings, { hideWhenZero: true })}
            {line(t('cash_flow.employee_payment_bonus'), values.potBonus, { hideWhenZero: true })}
            {line(t('cash_flow.employee_payment_employee_bonus'), values.bonuses, { hideWhenZero: true })}
            <div className="employee-payment__line is-subtotal">
                <span>{t('cash_flow.employee_payment_total_earnings')}</span>
                <strong>{convertDoubleToCurrencyPTBR(totalEarnings)}</strong>
            </div>
            {line(t('cash_flow.employee_payment_advances'), values.advances, { discount: true, hideWhenZero: true })}
            {line(t('cash_flow.employee_payment_consumptions'), values.consumptions, { discount: true, hideWhenZero: true })}
            <div className={`employee-payment__line is-net${netValue <= 0 ? ' is-negative' : ''}`}>
                <span>{t('cash_flow.employee_payment_net')}</span>
                <strong>{convertDoubleToCurrencyPTBR(netValue)}</strong>
            </div>
        </div>
    );
};

export default EmployeePaymentBreakdown;
